import {
  formatBytes,
  formatDuration,
  formatDurationApprox,
} from "@client/utils/format";
import { mobileWatchedThreshold } from "@lib/constants";
import { Badge } from "@sfc/ui";
import { CircleCheckIcon, CircleDashedIcon } from "lucide-react";

// How the mid-watch state is presented. Both variants read the same numbers;
// they differ in what they ask the reader to do with them.
//
// - "badge" (default): an accent capsule holding `elapsed / total`. The
//   iPhone app's video rows and Continue watching card.
// - "remaining": plain muted text saying how much is LEFT, for rows that draw
//   the fraction as a WatchProgressStripe on the poster instead. The bar is
//   the progress; the text carries the one thing the bar can't show. Used by
//   the funder workspace queue drawer.
export type VideoWatchInfoVariant = "badge" | "remaining";

// Watched/duration indicator shared by web and the iPhone app. Takes plain
// values (not a platform-specific video shape) so any caller can feed it.
export function VideoWatchInfo({
  watchProgress,
  durationSeconds,
  // Only used as a fallback label when duration is unknown (iPhone download
  // rows). Web meeting rows always have a duration, so they can omit it.
  sizeBytes = null,
  variant = "badge",
  completed,
}: {
  // Fraction in [0, 1].
  watchProgress: number;
  durationSeconds: number | null;
  sizeBytes?: number | null;
  variant?: VideoWatchInfoVariant;
  /**
   * Whether this video is FINISHED, from the server's sticky `completedAt`.
   *
   * Two different questions live in this component, and only one of them can
   * be answered by the playhead. `watchProgress` says WHERE THE READER IS —
   * "14 min left" is true whatever they have watched before. The word
   * "Watched" is a CLAIM ABOUT COMPLETION, and deriving that from position
   * gets it wrong the moment someone rewinds through a meeting they finished:
   * the To Do rail (which reads the sticky flag) called it done while this
   * called it 26%.
   *
   * Optional, and the playhead remains the fallback: callers that don't hold
   * the flag — the iPhone shell, which hydrates a store that doesn't carry
   * it — keep exactly their previous behaviour.
   */
  completed?: boolean;
}) {
  // Position drives everything except the completion claim.
  const watched = completed ?? watchProgress >= mobileWatchedThreshold;
  const inProgress = watchProgress > 0 && !watched;

  if (variant === "remaining") {
    return (
      <RemainingWatchInfo
        watchProgress={watchProgress}
        durationSeconds={durationSeconds}
        sizeBytes={sizeBytes}
        watched={watched}
        inProgress={inProgress}
      />
    );
  }

  const totalDuration = formatDuration(durationSeconds);
  const watchedDuration =
    inProgress && durationSeconds != null
      ? formatDuration(Math.floor(watchProgress * durationSeconds))
      : null;

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      {inProgress && watchedDuration != null && totalDuration != null ? (
        <Badge variant="accent-outline">{`${watchedDuration} / ${totalDuration}`}</Badge>
      ) : (
        <>
          {watched ? (
            <CircleCheckIcon className="size-2.5 text-success" />
          ) : (
            <CircleDashedIcon className="size-2.5" />
          )}
          {totalDuration ?? formatBytes(sizeBytes)}
        </>
      )}
    </div>
  );
}

function RemainingWatchInfo({
  watchProgress,
  durationSeconds,
  sizeBytes,
  watched,
  inProgress,
}: {
  watchProgress: number;
  durationSeconds: number | null;
  sizeBytes: number | null;
  watched: boolean;
  inProgress: boolean;
}) {
  // Rounded to the same minute the label shows, so "14 min left" can't sit
  // next to a bar drawn from a different number.
  const totalDuration = formatDurationApprox(durationSeconds);
  const remaining =
    inProgress && durationSeconds != null
      ? formatDurationApprox((1 - watchProgress) * durationSeconds)
      : null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
      {remaining != null ? (
        `${remaining} left`
      ) : watched ? (
        <>
          <CircleCheckIcon className="size-3 shrink-0 text-success" />
          {/* The check already says "watched", but the word survives at a
              glance where a 12px glyph doesn't — and the duration still
              matters for deciding whether to rewatch. */}
          <span className="truncate">
            Watched{totalDuration != null && ` · ${totalDuration}`}
          </span>
        </>
      ) : (
        // Nothing to signal on an unstarted row: no bar, no glyph, just how
        // long it is. (The badge variant shows a dashed circle here; that
        // marker only earns its place next to the capsule it balances.)
        (totalDuration ?? formatBytes(sizeBytes))
      )}
    </div>
  );
}
